/**
 * MixedScript, per `/specs/mixedscript.spec.md`. Everything in this package is a pure function
 * of its inputs — no clock, no randomness — because a plan has to come out the same on every
 * device, and the same as every other implementation of the language.
 */

/** Every failure the language reports carries where it happened. */
export class ScriptError extends Error {
  readonly line: number
  readonly column: number

  constructor(line: number, column: number, message: string) {
    super(message)
    this.name = 'ScriptError'
    this.line = line
    this.column = column
  }

  /** How the editor shows it: what went wrong, and where to look. */
  get located(): string {
    return `${this.message} (line ${this.line}, column ${this.column})`
  }
}

export type TokenKind = 'number' | 'text' | 'name' | 'punct' | 'hole' | 'newline' | 'end'

/** What a number is a quantity of. Weights are kilograms, times seconds, distances metres. */
export type Dim = 'none' | 'weight' | 'time' | 'distance'

/**
 * A number carries the unit it was written in. Values are held in the unit everything else in
 * the app is held in — kilograms, seconds, metres — and the source keeps what was written.
 */
export interface Unit {
  readonly suffix: string
  readonly dim: Dim
  readonly factor: number
}

export const UNITS = {
  reps: { suffix: '', dim: 'none', factor: 1 },
  ratio: { suffix: '%', dim: 'none', factor: 0.01 },
  kg: { suffix: 'kg', dim: 'weight', factor: 1 },
  lb: { suffix: 'lb', dim: 'weight', factor: 0.45359237 },
  seconds: { suffix: 's', dim: 'time', factor: 1 },
  minutes: { suffix: 'm', dim: 'time', factor: 60 },
  meters: { suffix: 'meters', dim: 'distance', factor: 1 },
  km: { suffix: 'km', dim: 'distance', factor: 1000 },
  miles: { suffix: 'miles', dim: 'distance', factor: 1609.344 },
} as const satisfies Record<string, Unit>

/** The suffixes a number can wear, longest first so `meters` is not read as `m` then `eters`. */
const SUFFIXES: readonly Unit[] = [
  UNITS.meters,
  UNITS.miles,
  UNITS.km,
  UNITS.kg,
  UNITS.lb,
  UNITS.seconds,
  UNITS.minutes,
]

export interface Token {
  readonly kind: TokenKind
  readonly text: string
  readonly line: number
  readonly column: number
  readonly number: number
  readonly unit: Unit
  /** Comment lines standing above this token, kept so an edit does not throw them away. */
  readonly comments: readonly string[]
}

const PUNCT3 = ['===', '!==']
const PUNCT2 = [
  '<=',
  '>=',
  '&&',
  '||',
  '??',
  '?.',
  '=>',
  '==',
  '!=',
  '++',
  '--',
  '+=',
  '-=',
  '*=',
  '/=',
]

const isDigit = (c: string) => c >= '0' && c <= '9'
const isLetter = (c: string) => /\p{L}/u.test(c)
const isLetterOrDigit = (c: string) => isDigit(c) || isLetter(c)

export function tokenize(source: string, allowHoles = false): Token[] {
  const tokens: Token[] = []
  let index = 0
  let line = 1
  let column = 1
  let pending: string[] = []

  /** The character at an offset, or `''` past the end — which no test of a character passes. */
  const peek = (offset = 0): string => source[index + offset] ?? ''

  function advance(): string {
    const c = peek()
    index++
    if (c === '\n') {
      line++
      column = 1
    } else {
      column++
    }
    return c
  }

  function add(
    kind: TokenKind,
    text: string,
    atLine: number,
    atColumn: number,
    value = 0,
    unit: Unit = UNITS.reps,
  ) {
    tokens.push({
      kind,
      text,
      line: atLine,
      column: atColumn,
      number: value,
      unit,
      comments: pending,
    })
    pending = []
  }

  while (index < source.length) {
    const c = peek()
    const startLine = line
    const startColumn = column

    if (c === '\n') {
      advance()
      // One break is a separator; a run of them is still one separator.
      if (tokens[tokens.length - 1]?.kind !== 'newline') {
        add('newline', '\n', startLine, startColumn)
      }
    } else if (c === ' ' || c === '\t' || c === '\r') {
      advance()
    } else if (c === '/' && peek(1) === '/') {
      let text = ''
      while (index < source.length && peek() !== '\n') {
        text += advance()
      }
      pending.push(text.replace(/^\/\//, '').trim())
    } else if (c === '"') {
      advance()
      let text = ''
      for (;;) {
        const next = peek()
        if (next === '' || next === '\n') {
          throw new ScriptError(startLine, startColumn, 'This text is never closed')
        }
        if (next === '"') {
          advance()
          break
        }
        if (next === '\\') {
          advance()
          const escaped = peek()
          if (escaped === '') {
            throw new ScriptError(line, column, 'Nothing to escape')
          }
          advance()
          text += escaped === 'n' ? '\n' : escaped
        } else {
          text += advance()
        }
      }
      add('text', text, startLine, startColumn)
    } else if (isDigit(c) || (c === '.' && isDigit(peek(1)))) {
      let digits = ''
      while (isDigit(peek()) || (peek() === '.' && isDigit(peek(1)))) {
        digits += advance()
      }
      const value = Number(digits)
      if (!Number.isFinite(value)) {
        throw new ScriptError(startLine, startColumn, 'This is not a number')
      }
      let unit: Unit = UNITS.reps
      if (peek() === '%') {
        advance()
        unit = UNITS.ratio
      } else if (isLetter(peek())) {
        let letters = ''
        while (isLetter(peek())) {
          letters += advance()
        }
        const found = SUFFIXES.find((candidate) => candidate.suffix === letters)
        if (!found) {
          throw new ScriptError(startLine, startColumn, `\`${letters}\` is not a unit`)
        }
        unit = found
      }
      add('number', digits, startLine, startColumn, value, unit)
    } else if (c === '$' && allowHoles) {
      advance()
      let name = ''
      while (isLetterOrDigit(peek()) || peek() === '_') {
        name += advance()
      }
      add('hole', name, startLine, startColumn)
    } else if (isLetter(c) || c === '_') {
      let name = ''
      while (isLetterOrDigit(peek()) || peek() === '_') {
        name += advance()
      }
      add('name', name, startLine, startColumn)
    } else {
      const three = source.slice(index, index + 3)
      const two = source.slice(index, index + 2)
      const punct = PUNCT3.includes(three) ? three : PUNCT2.includes(two) ? two : c
      for (const _ of punct) {
        advance()
      }
      add('punct', punct, startLine, startColumn)
    }
  }

  tokens.push({
    kind: 'end',
    text: '',
    line,
    column,
    number: 0,
    unit: UNITS.reps,
    comments: pending,
  })
  return tokens
}

export const isPunct = (token: Token, value: string) =>
  token.kind === 'punct' && token.text === value

export const isName = (token: Token, value: string) => token.kind === 'name' && token.text === value
